import { Tooltip } from '@mui/material'
import type { GridRenderCellParams, GridRenderEditCellParams, GridRowParams } from '@mui/x-data-grid'
import type { TFunction } from 'i18next'
import type { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import DataGridEditableTextField from '@/components/dataDisplay/DataGridEditableTextField'
import { WATTS_IN_MW } from '@/constants/units'
import OfferedBidPtusDataGridToolbar from '@/features/bidding/components/capacity/offer/OfferedBidPtusDataGridToolbar'
import MinMaxBiddableVolumesTooltip from '@/features/bidding/components/MinMaxBiddableVolumesTooltip'
import type { BidType, MarketProgram } from '@/features/bidding/constants'
import { MAX_PTU_ROWS_PER_DATAGRID } from '@/features/bidding/constants'
import { useBidOfferDataContext } from '@/features/bidding/contexts/BidOfferDataContext'
import { useResetPtusContext } from '@/features/bidding/contexts/ResetPtusContext'
import type { PtuConflict } from '@/features/bidding/hooks/useBidOfferPtuConflicts'
import type { BidMetaData, BulkFillMode, PtuForTheOfferVolumeDataGrid } from '@/features/bidding/types/bid'
import { convertToRoundedMw, round } from '@/features/bidding/utils/calculations/convertToRoundedMw'
import { getBidDisplayUnit } from '@/features/bidding/utils/getBidDisplayUnit'
import { isCapacityValid } from '@/features/bidding/utils/validation/isCapacityValid'

// Styles for disabled and warning rows
const dataGridStyles = {
  '& .ptu-disabled-row': {
    backgroundColor: '#f5f5f5 !important', // light grey
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: '#f5f5f5 !important',
    },
  },
  '& .ptu-about-to-be-disabled-row': {
    backgroundColor: '#fff9c4 !important', // light yellow
    '&:hover': {
      backgroundColor: '#fff9c4 !important',
    },
  },
  '& .ptu-underbid-row .MuiDataGrid-cell--editable': {
    color: '#d72e2e !important', // red
  },
  '& .ptu-reset-row': {
    backgroundColor: '#ffebee !important', // light red
    '&:hover': {
      backgroundColor: '#ffcdd2 !important',
    },
  },
  '& .ptu-conflict-row': {
    background:
      'repeating-linear-gradient(\n' +
      '  39deg,\n' +
      '  #FFF1F1,\n' +
      '  #FFF1F1 20px,\n' +
      '  #FFDFDF 20px,\n' +
      '  #FFDFDF 40px\n' +
      ')',
  },
}

type OfferedBidPtusDataGridProps = {
  acceptsOnlyPositiveInteger: boolean
  ptus: PtuForTheOfferVolumeDataGrid[]
  processRowUpdate: (
    newPtu: PtuForTheOfferVolumeDataGrid,
    oldPtu: PtuForTheOfferVolumeDataGrid,
  ) => Promise<PtuForTheOfferVolumeDataGrid>
  bidMetaData: BidMetaData
  loading?: boolean
  saveDraftCurrentValues: () => void
  volumeUnit: string
  onFillAll: (bulkMode: BulkFillMode, bulkFillValue: number | undefined) => void
}

function getRowClassName(
  params: GridRowParams,
  isPtuEditable: (ptuStart: DateTime, bidType: BidType, marketProgram: MarketProgram) => boolean,
  bidMetaData: BidMetaData,
  nextPtuToExpire: DateTime | null,
  isPtuMarkedAsResetForActivationGroup: (activationGroupUuid: string, ptuStart: DateTime) => boolean,
  isConflictivePtuForActivationGroup: (activationGroupUuid: string, ptuStart: DateTime) => boolean,
) {
  const ptuRow = params.row as PtuForTheOfferVolumeDataGrid
  const ptuStart = ptuRow.ptu.start
  const editable = isPtuEditable(ptuStart, bidMetaData.bidType, bidMetaData.marketProgram)

  const classes: string[] = []

  const wasReset = isPtuMarkedAsResetForActivationGroup(bidMetaData.activationGroupUuid, ptuStart)
  if (wasReset) {
    classes.push('ptu-reset-row')
  }

  if (
    ptuRow.acceptedCapacityVolume?.quantity &&
    ptuRow.acceptedCapacityVolume?.quantity > ptuRow.offeredVolume.quantity
  ) {
    classes.push('ptu-underbid-row')
  }

  if (!editable) {
    classes.push('ptu-disabled-row')
  }

  if (nextPtuToExpire && ptuStart.equals(nextPtuToExpire)) {
    classes.push('ptu-about-to-be-disabled-row')
  }

  if (isConflictivePtuForActivationGroup(bidMetaData.activationGroupUuid, ptuStart)) {
    classes.push('ptu-conflict-row')
  }

  return classes.length > 0 ? classes.join(' ') : ''
}

const onOfferChange =
  (params: GridRenderEditCellParams<PtuForTheOfferVolumeDataGrid>, isEditable: boolean) =>
  (value: string | undefined) => {
    // Prevent changes if PTU is not editable
    if (!isEditable) {
      return
    }

    if (value && !isCapacityValid(value)) {
      return
    }

    params.api.setEditCellValue({ ...params, value })
  }

const OfferedBidPtusDataGrid = ({
  acceptsOnlyPositiveInteger,
  ptus,
  processRowUpdate,
  loading,
  bidMetaData,
  saveDraftCurrentValues,
  volumeUnit,
  onFillAll,
}: Readonly<OfferedBidPtusDataGridProps>) => {
  const { t } = useTranslation()
  const { isPtuMarkedAsResetForActivationGroup } = useResetPtusContext()
  const { columnVisibility, ptuExpiration, conflictivePtusByGroup } = useBidOfferDataContext()

  // Get column visibility flags and PTU expiration data from context
  const { isValueStackingSupported, shouldDisplayCapacityBidReference, shouldShowAcceptedCapacityVolumeColumn } =
    columnVisibility.getColumnVisibility(bidMetaData.activationGroupUuid)
  const nextPtuToExpire = ptuExpiration.getNextPtuToExpire(bidMetaData.activationGroupUuid)
  const { isPtuEditable } = ptuExpiration

  const isConflictivePtuForActivationGroup = (activationGroupUuid: string, ptuStart: DateTime) => {
    const groupConflictivePtus = conflictivePtusByGroup.get(activationGroupUuid)
    if (!groupConflictivePtus) {
      return false
    }
    return groupConflictivePtus.some((ptuConflict: PtuConflict) => +ptuConflict.ptu.start === +ptuStart)
  }

  // Memoize columns to prevent recreating column definitions and tooltip elements on every render
  const columns = useMemo(
    () =>
      getColumns(
        t,
        volumeUnit,
        bidMetaData,
        acceptsOnlyPositiveInteger,
        isPtuMarkedAsResetForActivationGroup,
        isPtuEditable,
        nextPtuToExpire,
      ),
    [t, volumeUnit, bidMetaData, acceptsOnlyPositiveInteger, isPtuEditable, nextPtuToExpire],
  )

  return (
    <CustomDataGrid
      hideFooter
      columnVisibilityModel={{
        'ptu.suggestedBids': isValueStackingSupported,
        'ptu.offeredCapacityVolume': shouldDisplayCapacityBidReference,
        'ptu.acceptedCapacityVolume': shouldShowAcceptedCapacityVolumeColumn,
      }}
      columns={columns}
      getRowClassName={(params) =>
        getRowClassName(
          params,
          isPtuEditable,
          bidMetaData,
          nextPtuToExpire,
          isPtuMarkedAsResetForActivationGroup,
          isConflictivePtuForActivationGroup,
        )
      }
      getRowHeight={() => 52}
      getRowId={(row) => row.ptu.start.toString()}
      initialState={{ pagination: { paginationModel: { pageSize: MAX_PTU_ROWS_PER_DATAGRID, page: 0 } } }}
      isCellEditable={(params) => isPtuEditable(params.row.ptu.start, bidMetaData.bidType, bidMetaData.marketProgram)}
      isLoading={loading}
      processRowUpdate={processRowUpdate}
      rows={ptus ?? []}
      slotProps={{
        toolbar: {
          acceptsOnlyPositiveInteger,
          bidType: bidMetaData.bidType,
          deliveryDay: bidMetaData.deliveryDay,
          saveDraftCurrentValues,
          shouldHaveOptionForRatioOfAcceptedCapacity: shouldShowAcceptedCapacityVolumeColumn,
          shouldHaveOptionForRatioOfOfferedCapacity: shouldDisplayCapacityBidReference,
          shouldHaveOptionForRatioOfSuggested: isValueStackingSupported,
          onFillAll,
        },
      }}
      slots={{ toolbar: OfferedBidPtusDataGridToolbar }}
      sx={dataGridStyles}
      title={t('bidding.create_bid.new_bid')}
      titleExtraInfo={
        bidMetaData.activationGroupUuid ? (
          <MinMaxBiddableVolumesTooltip activationGroupUuid={bidMetaData.activationGroupUuid} />
        ) : undefined
      }
    />
  )
}

const getColumns = (
  t: TFunction<'translation'>,
  volumeUnit: string,
  bidMetaData: BidMetaData,
  acceptsOnlyPositiveInteger: boolean,
  isPtuMarkedAsResetForActivationGroup: (activationGroupUuid: string, ptuStart: DateTime) => boolean,
  isPtuEditable: (ptuStart: DateTime, bidType: BidType, marketProgram: MarketProgram) => boolean,
  nextPtuToExpire: DateTime | null,
) => [
  {
    field: 'ptu.start',
    headerName: t('bidding.create_bid.header.ptu'),
    flex: 1,
    valueFormatter: (value: DateTime) => value.toFormat('HH:mm'),
    valueGetter: (_, offeredAndForecastAndSuggestedPtu: PtuForTheOfferVolumeDataGrid) =>
      offeredAndForecastAndSuggestedPtu.ptu.start,
  },
  {
    field: 'ptu.forecastedWatts',
    headerName: t('bidding.create_bid.header.forecast'),
    sortable: false,
    valueFormatter: (value: number | null) => (value ? `${convertToRoundedMw(value)} MW` : '-'),
    valueGetter: (_, offeredAndForecastAndSuggestedPtu: PtuForTheOfferVolumeDataGrid) =>
      offeredAndForecastAndSuggestedPtu.forecastedVolume?.quantity ?? null,
    flex: 1,
  },
  {
    field: 'ptu.offeredCapacityVolume',
    headerName: t('bidding.create_bid.header.offered_capacity'),
    sortable: false,
    valueFormatter: (value: number | null) => (value ? `${convertToRoundedMw(value)} MW` : '-'),
    valueGetter: (_, offeredAndForecastAndSuggestedPtu: PtuForTheOfferVolumeDataGrid) =>
      offeredAndForecastAndSuggestedPtu.offeredCapacityVolume?.quantity ?? null,
    flex: 1,
  },
  {
    field: 'ptu.acceptedCapacityVolume',
    headerName: t('bidding.create_bid.header.accepted_capacity'),
    sortable: false,
    valueFormatter: (value: number | null) => (value ? `${convertToRoundedMw(value)} MW` : '-'),
    valueGetter: (_, offeredAndForecastAndSuggestedPtu: PtuForTheOfferVolumeDataGrid) =>
      offeredAndForecastAndSuggestedPtu.acceptedCapacityVolume?.quantity ?? null,
    flex: 1,
  },
  {
    field: 'ptu.suggestedBids',
    headerName: t('bidding.create_bid.header.suggested'),
    sortable: false,
    editable: false,
    valueFormatter: (value: number | null) => (value ? `${convertToRoundedMw(value)} MW` : '-'),
    valueGetter: (_, offeredAndForecastAndSuggestedPtu: PtuForTheOfferVolumeDataGrid) =>
      offeredAndForecastAndSuggestedPtu.suggestedVolume?.quantity ?? null,
    flex: 1,
  },
  {
    field: 'ptu.offeredWatts',
    headerName: t('bidding.create_bid.header.offer'),
    sortable: false,
    editable: true,
    valueFormatter: (value: number) => `${value ?? '-'} ${getBidDisplayUnit(volumeUnit)}`,
    valueGetter: (_, offeredAndForecastAndSuggestedPtu: PtuForTheOfferVolumeDataGrid) =>
      convertToRoundedMw(offeredAndForecastAndSuggestedPtu.offeredVolume.quantity),
    valueSetter: (
      value: number,
      offeredAndForecastAndSuggestedPtu: PtuForTheOfferVolumeDataGrid,
    ): PtuForTheOfferVolumeDataGrid => ({
      ...offeredAndForecastAndSuggestedPtu,
      offeredVolume: { quantity: value ? round(value * WATTS_IN_MW) : 0, unit: volumeUnit },
    }),
    flex: 1,
    renderCell: (params: GridRenderCellParams<PtuForTheOfferVolumeDataGrid>) => {
      const ptuStart = params.row.ptu.start
      const isEditable = isPtuEditable(ptuStart, bidMetaData.bidType, bidMetaData.marketProgram)
      const wasReset = isPtuMarkedAsResetForActivationGroup(bidMetaData.activationGroupUuid, ptuStart)
      const aboutToBeDisabled = !!nextPtuToExpire && ptuStart.equals(nextPtuToExpire)
      let tooltipTitle = ''
      if (wasReset) {
        tooltipTitle = t('bidding.ptu.was_reset')
      } else if (!isEditable) {
        tooltipTitle = t('bidding.ptu.not_editable')
      } else if (aboutToBeDisabled) {
        tooltipTitle = t('bidding.ptu.about_to_be_disabled')
      }

      const fieldElement = (
        <DataGridEditableTextField readonlyField disabled={!isEditable} value={params.formattedValue} />
      )

      return tooltipTitle ? (
        <Tooltip arrow title={tooltipTitle}>
          <span>{fieldElement}</span>
        </Tooltip>
      ) : (
        fieldElement
      )
    },
    renderEditCell: (params: GridRenderEditCellParams<PtuForTheOfferVolumeDataGrid>) => {
      const ptuStart = params.row.ptu.start
      const isEditable = isPtuEditable(ptuStart, bidMetaData.bidType, bidMetaData.marketProgram)

      // Automatically stop edit mode if PTU is not editable
      if (!isEditable) {
        setTimeout(() => {
          params.api.stopCellEditMode({ id: params.id, field: params.field })
        }, 0)
      }

      return (
        <DataGridEditableTextField
          acceptsOnlyPositiveInteger={acceptsOnlyPositiveInteger}
          disabled={!isEditable}
          value={params.value}
          onBlur={() => {
            params.api.stopCellEditMode({ id: params.id, field: params.field })
          }}
          onChange={onOfferChange(params, isEditable)}
        />
      )
    },
  },
]

export default OfferedBidPtusDataGrid
